import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdentity } from '../hooks/useIdentity';
import {
  isPairingSatisfied,
  hasRemotePair,
  getStoredPairs,
  removePair,
  updatePairLabel,
} from '../utils/pairing';
import { usePairingConnected } from '../hooks/usePairingConnected';
import { getStoredRelays } from '../utils/relay';
import { isInIframe, sendToChannel } from '../utils/iframeChannel';
import type { LocalIdentity, PairConfig } from '../types/identity';
import { PairingSetupBlock } from './PairingSetupBlock';

function logHomeStatus(
  identity: LocalIdentity,
  pairingSatisfied: boolean,
  relayStatus: string,
  relayCount: number,
): void {
  const name = identity.name !== undefined ? `\nNom: ${identity.name}` : '';
  const pairingLine = pairingSatisfied
    ? 'Satisfait: Oui'
    : 'Satisfait: Non\n⚠️ Pairing obligatoire avant de pouvoir se connecter';
  const msg = [
    '[UserWallet] Home — statut (log):',
    'UserWallet Login',
    'Statut identité',
    'Présente: Oui',
    `Clé publique: ${identity.publicKey.slice(0, 16)}...`,
    name,
    'Statut pairing',
    'Requis: Oui',
    pairingLine,
    'Statut réseau relais',
    `Statut: ${relayStatus}`,
    relayCount > 0 ? `Nombre de relais: ${relayCount}` : '',
  ]
    .filter(Boolean)
    .join('\n');
  console.warn(msg);
}

export function HomeScreen(): JSX.Element {
  const navigate = useNavigate();
  const { identity, isLoading } = useIdentity();
  const { connected: pairingConnected } = usePairingConnected();
  const pairingSatisfied = isPairingSatisfied();
  const hasRemote = hasRemotePair();
  const showSetupBlock = !hasRemote;
  const [pairs, setPairs] = useState<PairConfig[]>(() => getStoredPairs());
  const [addingDevice, setAddingDevice] = useState(false);
  const relays = getStoredRelays();
  const relayStatus = relays.length > 0 ? 'OK' : 'Non configuré';
  const pairingSetupRef = useRef<HTMLElement | null>(null);
  const hasScrolledAndLoggedRef = useRef(false);

  const refreshPairs = (): void => {
    setPairs(getStoredPairs());
  };

  useEffect(() => {
    if (identity === null) {
      return;
    }
    logHomeStatus(identity, pairingSatisfied, relayStatus, relays.length);
  }, [identity, pairingSatisfied, relayStatus, relays.length]);

  useEffect(() => {
    if (!isInIframe()) {
      return;
    }
    sendToChannel({
      type: 'pairing-relay-status',
      payload: {
        pairingSatisfied,
        relayOk: relayStatus === 'OK',
      },
    });
  }, [pairingSatisfied, relayStatus]);

  useEffect(() => {
    if (!showSetupBlock || identity === null || hasScrolledAndLoggedRef.current) {
      if (!showSetupBlock) {
        hasScrolledAndLoggedRef.current = false;
      }
      return;
    }
    hasScrolledAndLoggedRef.current = true;
    console.warn('[UserWallet] Redirection vers la section QR (pairing-setup)');
    window.location.hash = 'pairing-setup';
    const scrollToSection = (): void => {
      pairingSetupRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    };
    const t = window.setTimeout(scrollToSection, 100);
    return () => window.clearTimeout(t);
  }, [showSetupBlock, identity]);

  useEffect(() => {
    if (!addingDevice) {
      refreshPairs();
    }
  }, [addingDevice]);

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" aria-busy="true">
        Chargement...
      </div>
    );
  }

  if (identity === null) {
    return (
      <main>
        <p style={{ marginBottom: '1.5rem' }}>Identité locale absente</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <button onClick={() => navigate('/create-identity')}>
            Créer une identité locale
          </button>
          <button onClick={() => navigate('/import-identity')}>
            Importer une identité
          </button>
        </div>
      </main>
    );
  }

  const handleRemovePair = (pairUuid: string): void => {
    const ok = typeof window !== 'undefined' && window.confirm('Supprimer cet appareil du pairing ?');
    if (!ok) {
      return;
    }
    removePair(pairUuid);
    setPairs(getStoredPairs());
  };

  const handleLabelChange = (pairUuid: string, label: string): void => {
    updatePairLabel(pairUuid, label);
    refreshPairs();
  };

  const getDeviceDisplayLabel = (pair: PairConfig): string => {
    if (pair.label !== undefined && pair.label.trim() !== '') {
      return pair.label.trim();
    }
    if (pair.is_local) {
      return 'Appareil local';
    }
    const remoteIndex = pairs.filter((p) => !p.is_local).indexOf(pair) + 1;
    return `Appareil distant ${remoteIndex}`;
  };

  return (
    <main style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      {pairingConnected && (
        <p role="status" style={{ fontWeight: 'bold', color: 'var(--color-ok, var(--color-success))' }}>
          Connecté
        </p>
      )}
      <section
        ref={pairingSetupRef}
        id="pairing-setup"
        aria-labelledby="pairing-section-heading"
        style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
      >
        <h2 id="pairing-section-heading" style={{ marginTop: 0 }}>
          Pairing
        </h2>
        <p>
          <strong>Statut pairing</strong>
          <br />
          Requis: Oui
          <br />
          Satisfait: {pairingSatisfied ? 'Oui' : 'Non'}
        </p>
        {showSetupBlock && !addingDevice && (
          <PairingSetupBlock onDone={refreshPairs} isAddingDevice={false} />
        )}
        {hasRemote && !addingDevice && (
          <>
            <h3 id="devices-paired-heading">Appareils appariés</h3>
            <ul
              aria-labelledby="devices-paired-heading"
              style={{ listStyle: 'none', padding: 0, marginTop: '0.5rem' }}
            >
              {pairs.map((pair) => (
                <li
                  key={pair.uuid}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--color-background)',
                  }}
                >
                  <span
                    style={{
                      flex: '0 0 auto',
                      minWidth: '8rem',
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                    }}
                    aria-hidden="true"
                  >
                    {getDeviceDisplayLabel(pair)}
                  </span>
                  <input
                    id={`pair-label-${pair.uuid}`}
                    type="text"
                    value={pair.label ?? ''}
                    onChange={(e) => handleLabelChange(pair.uuid, e.target.value)}
                    onBlur={(e) => handleLabelChange(pair.uuid, e.target.value)}
                    placeholder={getDeviceDisplayLabel(pair)}
                    aria-label={
                      pair.is_local ? 'Éditer le label appareil local' : 'Éditer le label appareil distant'
                    }
                    style={{
                      flex: 1,
                      minWidth: 0,
                      padding: '0.25rem 0.5rem',
                      fontSize: '1rem',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--color-surface)',
                      color: 'var(--color-text)',
                    }}
                  />
                  {!pair.is_local && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemovePair(pair.uuid);
                      }}
                      aria-label={`Supprimer ${getDeviceDisplayLabel(pair)}`}
                      style={{
                        flexShrink: 0,
                        width: 'auto',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.875rem',
                        color: 'var(--color-error)',
                        backgroundColor: 'transparent',
                        border: '1px solid var(--color-error)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Supprimer
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <p style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setAddingDevice(true)}
                aria-label="Ajouter un appareil"
              >
                Ajouter un device
              </button>
            </p>
          </>
        )}
        {addingDevice && (
          <>
            <p>
              <button
                type="button"
                onClick={() => setAddingDevice(false)}
                aria-label="Annuler l'ajout d'un appareil"
              >
                Annuler
              </button>
            </p>
            <PairingSetupBlock
              onDone={() => {
                setAddingDevice(false);
                refreshPairs();
              }}
              isAddingDevice
            />
          </>
        )}
      </section>
    </main>
  );
}
